/**
 * 1.8.1 browser policy: Chromium browsers (Chrome, Edge) are fully supported; Safari and Firefox are best effort (share
 * links, approvals, the portal and the mobile pages work everywhere). One answer for every place that says so: the
 * editor's one-time note and Safari's note on clips with transparency.
 *
 * By engine: every browser on iPhone and iPad is Safari's (WebKit), whatever its name.
 */
export type BrowserSupport = 'chromium' | 'safari' | 'firefox' | 'other';

interface NavigatorLike { userAgent?: string; userAgentData?: { brands?: Array<{ brand: string }> } | null }

export function browserSupport(nav: NavigatorLike | undefined = typeof navigator === 'undefined' ? undefined : navigator as NavigatorLike): BrowserSupport {
  if (!nav) return 'other';
  const ua = nav.userAgent ?? '';
  // iOS and iPadOS: WebKit underneath (CriOS, FxiOS and EdgiOS too).
  if (/iPhone|iPad|iPod/.test(ua) || /CriOS|FxiOS|EdgiOS/.test(ua)) return 'safari';
  if (nav.userAgentData?.brands?.some((b) => b.brand === 'Chromium')) return 'chromium';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Chrome\/|Chromium\/|Edg\//.test(ua)) return 'chromium';
  if (/Safari\//.test(ua) && /AppleWebKit\//.test(ua)) return 'safari';
  return 'other';
}

/** The editor's note shows in Safari and Firefox, never in Chromium. */
export const editorNoteNeeded = (b: BrowserSupport): boolean => b === 'safari' || b === 'firefox';

/** The editor's note, once per browser: its dismissal is kept in localStorage (read and written in try/catch). */
export const EDITOR_BROWSER_NOTE_KEY = 'prism.editorBrowserNote.dismissed';

export function editorNoteDismissed(): boolean {
  try {
    return window.localStorage.getItem(EDITOR_BROWSER_NOTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissEditorNote(): void {
  try {
    window.localStorage.setItem(EDITOR_BROWSER_NOTE_KEY, '1');
  } catch {
    // Private mode or blocked storage: the note shows again next time.
  }
}
